import { ReducerRegistry } from '../base/redux';
import { _TRANSCRIBER_LEFT } from '../transcribing/actionTypes';

import {
    CLOSE_LANGUAGE_SELECTOR_MODAL,
    OPEN_LANGUAGE_SELECTOR_MODAL,
    REMOVE_CACHED_TRANSCRIPT_MESSAGE,
    REMOVE_TRANSCRIPT_MESSAGE,
    SET_DEFAULT_TRANSCRIPTION_LANGUAGE,
    SET_REQUESTING_SUBTITLES,
    TOGGLE_REQUESTING_CAPTIONS,
    TOGGLE_REQUESTING_SUBTITLES,
    UPDATE_TRANSCRIPT_MESSAGE
} from './actionTypes';

/**
 * Determines initial state for _requestingCaptionsModal based on AUTO_START_TRANSCRIBE.
 *
 * @returns {boolean}
 */
function _getInitialRequestingCaptionsModal() {
    try {
        if (typeof window !== 'undefined' && window.location) {
            const urlParams = new URLSearchParams(window.location.search);
            const autoStart = urlParams.get('AUTO_START_TRANSCRIBE') || urlParams.get('auto_start_transcribe');

            if (Number(autoStart) === 1) {
                return false;
            }
        }
        if (typeof config !== 'undefined' && Number(config?.AUTO_START_TRANSCRIBE) === 1) {
            return false;
        }
    } catch (e) {
        // Ignore errors in non-browser environments
    }

    return true;
}

/**
 * Default State for 'features/transcription' feature.
 */
const defaultState = {
    _cachedTranscriptMessages: new Map(),
    _displaySubtitles: false,
    _transcriptMessages: new Map(),
    _requestingSubtitles: false,
    _language: null,
    _requestingCaptionsModal: _getInitialRequestingCaptionsModal(),
    _showDefaultTranscriptionLang: false,
    isLanguageSelectorModalOpen: false
};

/**
 * Listen for actions for the transcription feature to be used by the actions
 * to update the rendered transcription subtitles.
 */
ReducerRegistry.register('features/subtitles', (
        state = defaultState, action) => {
    switch (action.type) {
    case REMOVE_TRANSCRIPT_MESSAGE:
        return _removeTranscriptMessage(state, action);
    case REMOVE_CACHED_TRANSCRIPT_MESSAGE:
        return _removeCachedTranscriptMessage(state, action);
    case UPDATE_TRANSCRIPT_MESSAGE:
        return _updateTranscriptMessage(state, action);
    case SET_REQUESTING_SUBTITLES:
        return {
            ...state,
            _displaySubtitles: action.displaySubtitles,
            _language: action.language,
            _requestingSubtitles: action.enabled
        };
    case TOGGLE_REQUESTING_SUBTITLES:
        return {
            ...state,
            _requestingSubtitles: !state._requestingSubtitles
        };

    case TOGGLE_REQUESTING_CAPTIONS:
        return {
            ...state,
            _requestingCaptionsModal: typeof action.requestingCaptionsModal === 'boolean'
                ? action.requestingCaptionsModal
                : !state._requestingCaptionsModal
        };
    case _TRANSCRIBER_LEFT:
        return {
            ...state,
            ...defaultState,
            _requestingCaptionsModal: _getInitialRequestingCaptionsModal()
        };
    case SET_DEFAULT_TRANSCRIPTION_LANGUAGE:
        return {
            ...state,
            _showDefaultTranscriptionLang: true
        };
    case OPEN_LANGUAGE_SELECTOR_MODAL:
        return {
            ...state,
            isLanguageSelectorModalOpen: true
        };
    case CLOSE_LANGUAGE_SELECTOR_MODAL:
        return {
            ...state,
            isLanguageSelectorModalOpen: false
        };
    }


    return state;
});

/**
 * Reduces a specific Redux action REMOVE_TRANSCRIPT_MESSAGE of the feature
 * transcription.
 *
 * @param {Object} state - The Redux state of the feature transcription.
 * @param {Action} action -The Redux action REMOVE_TRANSCRIPT_MESSAGE to reduce.
 * @returns {Object} The new state of the feature transcription after the
 * reduction of the specified action.
 */
function _removeTranscriptMessage(state, { transcriptMessageID }) {
    const newTranscriptMessages = new Map(state._transcriptMessages);
    const message = newTranscriptMessages.get(transcriptMessageID);
    let { _cachedTranscriptMessages } = state;

    if (message && !message.final) {
        _cachedTranscriptMessages = new Map(_cachedTranscriptMessages);
        _cachedTranscriptMessages.set(transcriptMessageID, message);
    }

    // Deletes the key from Map once a final message arrives.
    newTranscriptMessages.delete(transcriptMessageID);

    return {
        ...state,
        _cachedTranscriptMessages,
        _transcriptMessages: newTranscriptMessages
    };
}

/**
 * Reduces a specific Redux action REMOVE_CACHED_TRANSCRIPT_MESSAGE of the feature transcription.
 *
 * @param {Object} state - The Redux state of the feature transcription.
 * @param {Action} action -The Redux action REMOVE_CACHED_TRANSCRIPT_MESSAGE to reduce.
 * @returns {Object} The new state of the feature transcription after the reduction of the specified action.
 */
function _removeCachedTranscriptMessage(state,
        { transcriptMessageID }) {

    const newCachedTranscriptMessages = new Map(state._cachedTranscriptMessages);

    // Deletes the key from Map once a final message arrives.
    newCachedTranscriptMessages.delete(transcriptMessageID);

    return {
        ...state,
        _cachedTranscriptMessages: newCachedTranscriptMessages
    };
}

/**
 * Reduces a specific Redux action UPDATE_TRANSCRIPT_MESSAGE of the feature
 * transcription.
 *
 * @param {Object} state - The Redux state of the feature transcription.
 * @param {Action} action -The Redux action UPDATE_TRANSCRIPT_MESSAGE to reduce.
 * @returns {Object} The new state of the feature transcription after the
 * reduction of the specified action.
 */
function _updateTranscriptMessage(state,
        { transcriptMessageID, newTranscriptMessage }) {
    const newTranscriptMessages = new Map(state._transcriptMessages);

    const _cachedTranscriptMessages = new Map(state._cachedTranscriptMessages);

    _cachedTranscriptMessages.delete(transcriptMessageID);

    // Updates the new message for the given key in the Map.
    newTranscriptMessages.set(transcriptMessageID, newTranscriptMessage);

    return {
        ...state,
        _cachedTranscriptMessages,
        _transcriptMessages: newTranscriptMessages
    };
}
